// The record that lets an interrupted drop upload resume after a reload,
// a crash or a reboot (docs/adr/0015). It's the one place a link key is kept
// at rest on the sender's device, and only until the upload finishes or the
// drop ends (threat model T29). Never the passphrase: a drop with one asks
// for it again. IndexedDB, not localStorage, so it never rides along with
// anything sent to a server.

const DB_NAME = 'sss';
const STORE = 'dropUploads';
const KEY = 'current';

export interface ResumeFile {
  name: string;
  size: number;
  lastModified: number;
}

export interface DropResume {
  id: string;
  uploadToken: string;
  senderToken: string;
  /** base64url, as in the link. */
  linkKey: string;
  header: string;
  sealedManifest: string;
  hasPassphrase: boolean;
  files: ResumeFile[];
  parts: number;
  partSize: number;
  partsDone: number[];
  keepSeconds: number;
  maxDownloads: number;
  uploadDeadline: string;
}

const open = (): Promise<IDBDatabase> =>
  new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const run = async <T>(mode: IDBTransactionMode, act: (store: IDBObjectStore) => IDBRequest): Promise<T> => {
  const db = await open();
  try {
    return await new Promise<T>((resolve, reject) => {
      const req = act(db.transaction(STORE, mode).objectStore(STORE));
      req.onsuccess = () => resolve(req.result as T);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
};

/** The unfinished upload in this browser, if any. Storage being unavailable (a private window) reads as none. */
export const loadDropResume = async (): Promise<DropResume | null> => {
  try {
    const found = await run<DropResume | undefined>('readonly', (s) => s.get(KEY));
    // Past its deadline the server has ended it; nothing to resume.
    if (found && Date.parse(found.uploadDeadline) <= Date.now()) {
      await forgetDropResume();
      return null;
    }
    return found ?? null;
  } catch {
    return null;
  }
};

export const saveDropResume = async (record: DropResume): Promise<void> => {
  try {
    await run('readwrite', (s) => s.put(record, KEY));
  } catch {
    // Without storage the upload still works; it just can't resume after a reload.
  }
};

export const forgetDropResume = async (): Promise<void> => {
  try {
    await run('readwrite', (s) => s.delete(KEY));
  } catch {
    // Nothing kept, nothing to forget.
  }
};

/** Whether `files` are the ones the upload started with: same names, sizes and modification times, in order. */
export const sameFiles = (expected: ResumeFile[], files: File[]): boolean =>
  expected.length === files.length && expected.every((f, i) => files[i]?.name === f.name && files[i]?.size === f.size && files[i]?.lastModified === f.lastModified);

/*
 * A started download, so a reload carries on in the same 24-hour window
 * without using another download (docs/adr/0015). It holds the link key,
 * because the page takes the key out of the address bar once read. Deleted
 * when the download finishes, or once its window has closed.
 */
export interface DownloadResume {
  id: string;
  linkKey: string;
  downloadToken: string;
  windowEndsAt: string;
}

const downloadKey = (id: string) => `download:${id}`;

export const loadDownloadResume = async (id: string): Promise<DownloadResume | null> => {
  try {
    const found = await run<DownloadResume | undefined>('readonly', (s) => s.get(downloadKey(id)));
    if (found && Date.parse(found.windowEndsAt) <= Date.now()) {
      await forgetDownloadResume(id);
      return null;
    }
    return found ?? null;
  } catch {
    return null;
  }
};

export const saveDownloadResume = async (record: DownloadResume): Promise<void> => {
  try {
    await run('readwrite', (s) => s.put(record, downloadKey(record.id)));
  } catch {
    // Without storage a reload starts a new download; nothing else changes.
  }
};

export const forgetDownloadResume = async (id: string): Promise<void> => {
  try {
    await run('readwrite', (s) => s.delete(downloadKey(id)));
  } catch {
    // Nothing kept.
  }
};
